import { useEffect, useMemo, useState } from "react";
import { AGGridDefault, ModalTable, Pagination } from "../../../../components";
import { ColDef } from "ag-grid-community";
import {
  IQueryPosition,
  IQueryPositionDetail,
} from "../../../../interfaces/IQuery";
import { useAuth } from "../../../../context/auth";
import { ERole } from "../../../../interfaces/User/newUser";
import { useTranslation } from "react-i18next";
import { TagCapsules } from "../../../../components";
import { IColor } from "../../../../utils/selectColors";
import { EOrderSide } from "../../../../interfaces/IQuery";
import dataProcessing from "../../../../utils/dataProcessing";
import { convertToDateAndHour } from "../../../../utils/date";
import { orderStatusInfo } from "../../../../utils/OrderStatusInfo";
import {
  useAnalystOrderDetail,
  useCustomerOrderDetail,
} from "../../../../hooks";

interface IProps {
  query: IQueryPosition;
  id: string;
  setClose: () => void;
}

export default function Modaldetalhes({ query, id, setClose }: IProps) {
  const { t } = useTranslation();
  const { roleSelected } = useAuth();
  const isAnalytic = roleSelected === ERole.analyst;
  const [queryDetail, setQueryDetail] = useState<IQueryPositionDetail>({
    id: id,
    page: 1,
    pageSize: 300,
    isAnalytic: isAnalytic,
    client_id: query.client_id || null,
  });

  useEffect(() => {
    setQueryDetail((prev) => ({ ...prev, roleSelected: roleSelected }));
  }, [roleSelected]);

  useEffect(() => {
    if (!!query.client_id) {
      setQueryDetail((prev) => ({ ...prev, client_id: query.client_id }));
    } else {
      setQueryDetail((prev) => ({ ...prev, client_id: null }));
    }
  }, [query]);
  const {
    data: dataAnalystOrderDetail,
    isLoading: isLoadingAnalyst,
    isFetching: isFetchingAnalyst,
  } = useAnalystOrderDetail(queryDetail);
  const {
    data: dataCustumerOrderDetail,
    isLoading: isLoadingCustumer,
    isFetching: isFetchingCustumer,
  } = useCustomerOrderDetail(queryDetail);

  const dataOrderDetail = isAnalytic
    ? dataAnalystOrderDetail?.data
    : dataCustumerOrderDetail;

  const isLoading = isAnalytic ? isLoadingAnalyst : isLoadingCustumer;
  const isFetching = isAnalytic ? isFetchingAnalyst : isFetchingCustumer;

  const pagination = {
    page: queryDetail.page,
    pageSize: queryDetail.pageSize,
    totalElements: dataAnalystOrderDetail?.totalElements,
    totalPages: dataAnalystOrderDetail?.totalPages,
    view: dataAnalystOrderDetail?.data?.length || 0,
  };

  const columnDefs = useMemo<ColDef<any>[]>(
    () => [
      {
        headerName: t("operacoes.ordens.table.table_head.product_title"),
        field: "product_title",
        suppressColumnsToolPanel: true,
        width: 200,
        cellRenderer: (params: any) =>
          dataProcessing(params.data?.product_title),
      },
      {
        headerName: t("operacoes.ordens.table.table_head.symbol"),
        field: "symbol",
        suppressColumnsToolPanel: true,
        width: 120,
        cellRenderer: (params: any) => dataProcessing(params.data?.symbol),
      },
      {
        headerName: t("operacoes.ordens.table.table_head.order_status"),
        field: "order_status",
        suppressColumnsToolPanel: true,
        width: 130,
        cellRenderer: (params: any) => {
          const orderStatus = orderStatusInfo(params.data?.order_status);
          return params.data?.order_status ? (
            <TagCapsules mr={12} border colorTag={orderStatus.color}>
              {t(orderStatus.label)}
            </TagCapsules>
          ) : (
            "---"
          );
        },
      },
      {
        headerName: t("operacoes.ordens.table.table_head.order_side"),
        field: "order_side",
        suppressColumnsToolPanel: true,
        width: 110,
        cellRenderer: (params: any) => {
          return params.data?.order_side_text ? (
            <TagCapsules
              mr={12}
              border
              colorTag={
                params.data?.order_side === EOrderSide.BUY
                  ? IColor.orange
                  : IColor.green
              }
            >
              {params.data?.order_side_text}
            </TagCapsules>
          ) : (
            "---"
          );
        },
      },
      {
        headerName: t("operacoes.ordens.table.table_head.quantity"),
        field: "quantity",
        suppressColumnsToolPanel: true,
        width: 160,
        cellRenderer: (params: any) => dataProcessing(params.data?.quantity),
      },
      {
        headerName: t("operacoes.ordens.table.table_head.executed_price"),
        field: "executed_price",
        suppressColumnsToolPanel: true,
        width: 160,
        cellRenderer: (params: any) =>
          dataProcessing(params.data?.executed_price),
      },
      {
        headerName: t("operacoes.ordens.table.table_head.average_price"),
        field: "average_price",
        suppressColumnsToolPanel: true,
        width: 160,
        cellRenderer: (params: any) =>
          dataProcessing(params.data?.average_price),
      },
      {
        headerName: t("operacoes.ordens.table.table_head.partial_profit_loss"),
        field: "partial_profit_loss",
        suppressColumnsToolPanel: true,
        width: 100,
        cellRenderer: (params: any) =>
          dataProcessing(params.data?.partial_profit_loss),
      },
      {
        headerName: t("operacoes.ordens.table.table_head.profit_loss"),
        field: "profit_loss",
        suppressColumnsToolPanel: true,
        width: 100,
        cellRenderer: (params: any) => dataProcessing(params.data?.profit_loss),
      },
      {
        headerName: t("operacoes.ordens.table.table_head.clorderid"),
        field: "clorderid",
        suppressColumnsToolPanel: true,
        width: 155,
        cellRenderer: (params: any) => dataProcessing(params.data?.clorderid),
      },
      {
        headerName: t("operacoes.ordens.table.table_head.position_id"),
        field: "position_seq_num",
        suppressColumnsToolPanel: true,
        width: 70,
        cellRenderer: (params: any) =>
          dataProcessing(params.data?.position_seq_num),
      },
      {
        headerName: t("operacoes.ordens.table.table_head.side_opening"),
        field: "side_opening",
        suppressColumnsToolPanel: true,
        width: 110,
        cellRenderer: (params: any) => {
          return params.data?.side_opening_text ? (
            <TagCapsules
              mr={12}
              border
              colorTag={
                params.data?.side_opening === EOrderSide.BUY
                  ? IColor.orange
                  : IColor.green
              }
            >
              {params.data?.side_opening_text}
            </TagCapsules>
          ) : (
            "---"
          );
        },
      },
      {
        headerName: t("operacoes.ordens.table.table_head.organization_name"),
        field: "organization_name",
        suppressColumnsToolPanel: true,
        width: 100,
        cellRenderer: (params: any) =>
          dataProcessing(params.data?.organization_name),
      },
      {
        headerName: t("operacoes.ordens.table.table_head.sending_time"),
        field: "sending_time",
        suppressColumnsToolPanel: true,
        width: 160,
        cellRenderer: (params: any) =>
          convertToDateAndHour(params.data?.sending_time),
      },
      {
        headerName: t("operacoes.ordens.table.table_head.execution_time"),
        field: "execution_time",
        suppressColumnsToolPanel: true,
        width: 160,
        cellRenderer: (params: any) =>
          convertToDateAndHour(params.data?.execution_time),
      },
    ],
    [t]
  );

  return (
    <>
      <ModalTable
        open={!!id}
        setClose={setClose}
        title={t("operacoes.details") || "Detalhes"}
        full={true}
      >
        <AGGridDefault
          title={
            isAnalytic
              ? t("operacoes.ordens.table.orders_opens_client")
              : t("operacoes.ordens.table.orders_open_analyst")
          }
          rowData={dataOrderDetail || []}
          columnDefs={columnDefs}
          isLoading={isLoading || isFetching}
          pagination={
            isAnalytic ? (
              <Pagination
                pagination={pagination}
                onChange={(page) => {
                  setQueryDetail((prev) => ({ ...prev, page: page }));
                }}
              />
            ) : undefined
          }
          height={isAnalytic ? "450px" : "106px"}
          storageKey={`columns-orders-modal-${
            isAnalytic ? "analyst" : "customer"
          }`}
          context={{
            query: queryDetail,
            setQuery: setQueryDetail,
            hasFilters: isAnalytic,
          }}
        />
      </ModalTable>
    </>
  );
}
